import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiClient } from '@/lib/apiClient'
import { API_ROUTES, PAGINATION } from '@/constants/apiRoutes'
import {
  CreateNotificationProcessing,
  NotificationProcessingAction,
  NotificationProcessingAddResponse,
  NotificationProcessingList,
} from '@/types/index'

export const getNotificationProcessings = createAsyncThunk<NotificationProcessingList, { certIds: string[], userIds: string[], page: number, pageSize: number, referenceDate?: string }>(API_ROUTES.NOTIFICATION_PROCESSING_GET_ENDPOINT, async ({ certIds, userIds, page, pageSize, referenceDate }) => {
  const res = await apiClient.post<NotificationProcessingList>(API_ROUTES.NOTIFICATION_PROCESSING_GET_ENDPOINT, {
    cert_ids: certIds,
    user_ids: userIds,
    page: page,
    page_size: pageSize,
    reference_date: referenceDate || undefined,
  },
  )
  return res
})
export const sendNotificationProcessings = createAsyncThunk<
  NotificationProcessingAction,
  { certIds: string[], userIds: string[], imageIds: number[] },
  { rejectValue: string }
>(
  API_ROUTES.NOTIFICATION_PROCESSING_SEND_ENDPOINT,
  async ({ certIds, userIds, imageIds }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post<NotificationProcessingAction>(API_ROUTES.NOTIFICATION_PROCESSING_SEND_ENDPOINT, {
        cert_ids: certIds,
        user_ids: userIds,
        image_ids: imageIds
      },
      )
      return res
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.error?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        '通知送信に失敗しました。'
      )
    }
  }
)
export const addNotificationProcessings = createAsyncThunk<
  NotificationProcessingAddResponse,
  { parameter: CreateNotificationProcessing[] },
  { rejectValue: string }
>(
  API_ROUTES.NOTIFICATION_PROCESSING_ADD_ENDPOINT,
  async ({ parameter }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post<NotificationProcessingAddResponse>(API_ROUTES.NOTIFICATION_PROCESSING_ADD_ENDPOINT, {
        items: parameter
      },
      )
      return res
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.error?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        '通知先の追加に失敗しました。'
      )
    }
  }
)

const initialState: NotificationProcessingList = {
  items: [],
  message: '',
  page: PAGINATION.PAGE,
  page_size: PAGINATION.PAGE_SIZE,
  status: '',
  total: 0,
  loading: false,
  error: null,
  yellow_count: 0,
  red_count: 0,
}

const notificationProcessingSlice = createSlice({
  name: 'notificationProcessing',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // GET
      .addCase(getNotificationProcessings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getNotificationProcessings.fulfilled, (state, action) => {
        state.items = action.payload.items
        state.message = action.payload.message
        state.page = action.payload.page
        state.page_size = action.payload.page_size
        state.status = action.payload.status
        state.total = action.payload.total
        state.yellow_count = action.payload.yellow_count ?? 0
        state.red_count = action.payload.red_count ?? 0
        state.loading = false
      })
      .addCase(getNotificationProcessings.rejected, (state, action) => {
        state.error = action.error.message || 'Error'
        state.loading = false
      })
      // SEND
      .addCase(sendNotificationProcessings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(sendNotificationProcessings.fulfilled, (state, action) => {
        state.message = action.payload.message
        state.status = action.payload.status
        state.loading = false
      })
      .addCase(sendNotificationProcessings.rejected, (state, action) => {
        state.error = action.payload || action.error.message || 'Error'
        state.loading = false
      })
      // ADD
      .addCase(addNotificationProcessings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addNotificationProcessings.fulfilled, (state, action) => {
        state.message = action.payload.message
        state.status = action.payload.status
        state.loading = false
      })
      .addCase(addNotificationProcessings.rejected, (state, action) => {
        state.error = action.payload || action.error.message || 'Error'
        state.loading = false
      })
  },
})

export default notificationProcessingSlice.reducer
